/**
 * Sync Status Widget Component
 * 
 * A compact widget showing GMB sync status that can be embedded
 * in other pages like GMB Overview, Reviews, Posts, etc.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { 
  RefreshCw, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  Activity,
  ChevronRight,
  Loader2
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { GmbSyncLog, GmbSyncType } from '@/types/gmb'

interface SyncStatusWidgetProps {
  /** Which sync type to display (optional, shows overall status if not provided) */
  syncType?: GmbSyncType
  /** Show detailed view */
  detailed?: boolean
  /** Enable quick sync button */
  enableQuickSync?: boolean
  /** Callback for quick sync */
  onQuickSync?: (syncType: GmbSyncType) => Promise<void>
  /** Refresh interval in milliseconds */
  refreshInterval?: number
}

export function SyncStatusWidget({
  syncType,
  detailed = false,
  enableQuickSync = false,
  onQuickSync,
  refreshInterval = 60000 // 1 minute default
}: SyncStatusWidgetProps) {
  const navigate = useNavigate()
  const [latestSync, setLatestSync] = useState<GmbSyncLog | null>(null)
  const [activeSyncs, setActiveSyncs] = useState<GmbSyncLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)

  useEffect(() => {
    loadSyncStatus()
    const interval = setInterval(loadSyncStatus, refreshInterval)
    return () => clearInterval(interval)
  }, [syncType, refreshInterval, loadSyncStatus])

  const loadSyncStatus = useCallback(async () => {
    try {
      // Get active syncs
      const { data: active, error: activeError } = await supabase
        .from('gmb_sync_log')
        .select('*')
        .eq('status', 'in_progress')
        .order('started_at', { ascending: false })

      if (activeError) {
        console.warn('Failed to fetch active syncs:', activeError)
      } else if (active) {
        setActiveSyncs(active)
      }

      // Get latest completed sync
      let query = supabase
        .from('gmb_sync_log')
        .select('*')
        .neq('status', 'in_progress')
        .order('started_at', { ascending: false })
        .limit(1)

      if (syncType && syncType !== 'all') {
        query = query.eq('sync_type', syncType)
      }

      const { data: latest } = await query

      if (latest && latest.length > 0) {
        setLatestSync(latest[0])
      }

      setIsLoading(false)
    } catch (error) {
      console.error('Error loading sync status:', error)
      setIsLoading(false)
    }
  }, [syncType])

  const handleQuickSync = async () => {
    if (!onQuickSync || !syncType) return
    
    setIsSyncing(true)
    try {
      await onQuickSync(syncType)
      await loadSyncStatus()
    } finally {
      setIsSyncing(false)
    }
  }

  const navigateToSyncPage = () => {
    navigate('/integrations/gmb/sync')
  }

  // Check if this sync type is currently active
  const isCurrentlySyncing = activeSyncs.some(
    sync => !syncType || sync.sync_type === syncType
  )

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Calculate sync health
  const getSyncHealth = () => {
    if (!latestSync) return { status: 'unknown', message: 'No sync history' }
    
    const hoursSinceSync = latestSync.completed_at 
      ? (Date.now() - new Date(latestSync.completed_at).getTime()) / (1000 * 60 * 60)
      : 999

    if (latestSync.status === 'failed') {
      return { status: 'error', message: 'Last sync failed' }
    }
    
    if (hoursSinceSync < 1) {
      return { status: 'good', message: 'Recently synced' }
    }
    
    if (hoursSinceSync < 24) {
      return { status: 'good', message: 'Synced today' }
    }
    
    if (hoursSinceSync < 48) {
      return { status: 'warning', message: 'Synced yesterday' }
    }
    
    return { status: 'warning', message: 'Needs sync' }
  }

  const syncHealth = getSyncHealth()

  return (
    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={navigateToSyncPage}>
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">
                {syncType ? `${syncType.charAt(0).toUpperCase() + syncType.slice(1)} Sync` : 'GMB Sync Status'}
              </span>
            </div>
            
            {isCurrentlySyncing ? (
              <Badge variant="outline" className="animate-pulse">
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                Syncing
              </Badge>
            ) : (
              <Badge 
                variant={
                  syncHealth.status === 'good' ? 'default' : 
                  syncHealth.status === 'warning' ? 'secondary' : 
                  'destructive'
                }
              >
                {syncHealth.status === 'good' && <CheckCircle className="h-3 w-3 mr-1" />}
                {syncHealth.status === 'warning' && <Clock className="h-3 w-3 mr-1" />}
                {syncHealth.status === 'error' && <AlertCircle className="h-3 w-3 mr-1" />}
                {syncHealth.message}
              </Badge>
            )}
          </div>

          {/* Active Sync Progress */}
          {isCurrentlySyncing && activeSyncs.length > 0 && (
            <div className="space-y-1">
              <Progress value={50} className="h-1" />
              <p className="text-xs text-muted-foreground">
                Processing {activeSyncs[0].sync_type}...
              </p>
            </div>
          )}

          {/* Last Sync Info */}
          {latestSync && !isCurrentlySyncing && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Last sync:</span>
                <span>
                  {latestSync.completed_at
                    ? formatDistanceToNow(new Date(latestSync.completed_at), { addSuffix: true })
                    : 'In progress'}
                </span>
              </div>
              
              {detailed && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Items synced:</span>
                    <span className="font-medium">
                      {latestSync.items_synced}
                      {latestSync.items_failed > 0 && (
                        <span className="text-red-600 ml-1">
                          ({latestSync.items_failed} failed)
                        </span>
                      )}
                    </span>
                  </div>
                  
                  {latestSync.completed_at && latestSync.started_at && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Duration:</span>
                      <span>
                        {Math.round(
                          (new Date(latestSync.completed_at).getTime() - 
                           new Date(latestSync.started_at).getTime()) / 1000
                        )}s
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t">
            {enableQuickSync && onQuickSync && syncType ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  handleQuickSync()
                }}
                disabled={isSyncing || isCurrentlySyncing}
                className="h-7"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                Quick Sync
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                Click to manage syncs
              </span>
            )}
            
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Mini sync indicator for embedding in page headers
 */
export function SyncStatusIndicator({ syncType }: { syncType?: GmbSyncType }) {
  const [status, setStatus] = useState<'syncing' | 'synced' | 'error' | 'unknown'>('unknown')
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null)

  useEffect(() => {
    const checkStatus = async () => {
      // Check for active syncs
      const { data: active } = await supabase
        .from('gmb_sync_log')
        .select('*')
        .eq('status', 'in_progress')
        .eq('sync_type', syncType || 'all')
        .single()

      if (active) {
        setStatus('syncing')
        return
      }

      // Get last completed sync
      const { data: latest } = await supabase
        .from('gmb_sync_log')
        .select('*')
        .eq('sync_type', syncType || 'all')
        .neq('status', 'in_progress')
        .order('started_at', { ascending: false })
        .limit(1)
        .single()

      if (latest) {
        setStatus(latest.status === 'success' ? 'synced' : 'error')
        setLastSyncTime(latest.completed_at ? new Date(latest.completed_at) : null)
      }
    }

    checkStatus()
    const interval = setInterval(checkStatus, 30000) // Check every 30 seconds
    return () => clearInterval(interval)
  }, [syncType])

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {status === 'syncing' && (
        <>
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Syncing...</span>
        </>
      )}
      {status === 'synced' && lastSyncTime && (
        <>
          <CheckCircle className="h-3 w-3 text-green-600" />
          <span>
            Synced {formatDistanceToNow(lastSyncTime, { addSuffix: true })}
          </span>
        </>
      )}
      {status === 'error' && (
        <>
          <AlertCircle className="h-3 w-3 text-red-600" />
          <span>Sync error</span>
        </>
      )}
    </div>
  )
}